'use client';

import { useEffect } from 'react';

/**
 * The last-resort error boundary, for a throw in the ROOT LAYOUT itself.
 *
 * src/app/error.tsx handles everything below the layout and keeps the site
 * chrome around the message. It cannot catch the layout, the providers, or the
 * theme/auth/geo contexts they mount, because those are what wrap it; a throw
 * there takes the boundary down with the tree. Next routes that case here
 * instead, and this component REPLACES the root layout, which is why it renders
 * its own <html> and <body>: there is no document otherwise.
 *
 * WRITTEN TO BE UNABLE TO FAIL. No imports beyond React, no UI components, no
 * Tailwind classes, no context, no icons, no fonts. Every one of those is
 * reached through the layout or the design system, and this file only ever runs
 * in the situation where something on that path has already thrown, so a
 * dependency here is a second failure with nowhere left to fall. Styling is
 * inline, plus one <style> element for the dark-mode variant; inline styles
 * cannot carry a media query, and the CSP allows 'unsafe-inline' for style-src
 * (never for script-src, which is nonce-only, so there is no script here at all).
 *
 * The reload is a plain <a href="/"> rather than reset() or a router call: if
 * the providers are what threw, re-rendering into the same broken tree just
 * reproduces the fault, whereas a full document load re-runs the whole app.
 */
export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    console.error('[app/global-error] root layout failed', error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#ffffff',
          color: '#111111',
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        <style>{`
          @media (prefers-color-scheme: dark) {
            body { background: #0a0a0a !important; color: #fafafa !important; }
            .ge-card { border-color: #262626 !important; background: #141414 !important; }
            .ge-muted { color: #a3a3a3 !important; }
            .ge-btn { background: #fafafa !important; color: #0a0a0a !important; }
          }
        `}</style>
        <div
          className="ge-card"
          style={{
            maxWidth: '420px',
            width: '100%',
            border: '1px solid #e5e5e5',
            background: '#fafafa',
            borderRadius: '12px',
            padding: '32px',
          }}
        >
          <p
            className="ge-muted"
            style={{
              margin: '0 0 12px',
              fontSize: '12px',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#737373',
            }}
          >
            Error
          </p>
          <h1 style={{ margin: '0 0 12px', fontSize: '22px', lineHeight: 1.3 }}>
            supa.place couldn’t load
          </h1>
          <p
            className="ge-muted"
            style={{ margin: '0 0 20px', fontSize: '14px', lineHeight: 1.6, color: '#525252' }}
          >
            Something went wrong starting the site. Reloading usually fixes it. If it
            keeps happening, email{' '}
            <a href="mailto:support@supa.place" style={{ color: 'inherit' }}>
              support@supa.place
            </a>
            .
          </p>
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages --
              A plain <a> is REQUIRED here, not an oversight. next/link does a
              client-side navigation through the router and re-renders into the
              same React tree: the tree whose root layout has just thrown, so it
              would reproduce the fault rather than escape it. A full document
              load is the only thing that re-runs the providers from scratch.
              Importing Link would also put a dependency in the one file that has
              to be able to render when the app cannot. */}
          <a
            className="ge-btn"
            href="/"
            style={{
              display: 'inline-block',
              background: '#111111',
              color: '#ffffff',
              padding: '10px 18px',
              borderRadius: '8px',
              fontSize: '14px',
              textDecoration: 'none',
            }}
          >
            Reload supa.place
          </a>
          {error.digest && (
            <p
              className="ge-muted"
              style={{ margin: '20px 0 0', fontSize: '12px', color: '#737373' }}
            >
              Reference:{' '}
              <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                {error.digest}
              </span>
            </p>
          )}
        </div>
      </body>
    </html>
  );
}
